import { ActivatedRoute, Router } from '@angular/router';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { DxcDropdownOption, DxcDropdownOptionValue } from '@dxc/ui-components/dropdown';
import {
  EligbilityFormType,
  EligibilityService
} from '@app/core/services/eligibility/eligibility.service';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { Subject, Subscription, throttleTime } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AppConstants } from '@app/core/constants/app.constants';
import { AppService } from '@app/core/services/app/app.service';
import { AppointmentService } from '@app/core/services/appointment/appointment.service';
import { AuthService } from '@app/core/services/auth/auth.service';
import { AutoUnsubscribe } from '@app/shared/utils/auto-unsub.decorator';
import { CheckEligibilityComponent } from '../check-eligibility/check-eligibility.component';
import { DateTimeUtils } from '@app/shared/utils/date-time.utils';
import { DxcFormValidators } from '@app/shared/utils/form-validators';
import { DxcIcon } from '@dxc/ui-components/icon/icon-types';
import { EligibilityCompleteService } from '@app/core/services/eligibility/eligibility-complete.service';
import { Logger } from '@app/shared/utils/logger.utils';
import { Payer } from '@app/core/models/payer.model';
import { ProcedureCode } from '@app/core/models/procedure-code.model';
import { Provider } from '@app/core/models/provider.model';
import { SharedService } from '@app/core/services/shared/shared.service';
import { UrlConstants } from '@app/core/constants/url.constants';
import { UserData } from '@app/core/models/userdata.model';
import { environment } from 'projects/portal-ui/src/environments/environment';

const SIDENAV_BREAKPOINT = 768;
const usZipCodeRegex = /^\d{5}(?:-\d{4})?$/;

export const ELIGIBILITY_FORM_VALIDATORS = {
  firstName: [Validators.required],
  lastName: [Validators.required],
  dateOfBirth: [Validators.required],
  zipCode: [Validators.required, Validators.pattern(usZipCodeRegex)],
  memberId: [Validators.required, Validators.minLength(3)],
  groupNumber: [],
  appointmentDate: [
    Validators.required,
    DxcFormValidators.date,
    DxcFormValidators.minDate(DateTimeUtils.startOfToday())
  ],
  appointmentTime: [Validators.required],
  provider: [Validators.required],
  providerName: [],
  providerNpi: [],
  providerTaxId: [],
  insurancePayer: [Validators.required],
  procedureCodes: [],
  relationshipToSubscriber: [Validators.required],
  tags: []
};

@Component({
  selector: 'app-app-container',
  templateUrl: './app-container.component.html',
  styleUrls: ['./app-container.component.scss']
})
@AutoUnsubscribe()
@UntilDestroy()
export class AppContainerComponent implements OnInit {
  @ViewChild(CheckEligibilityComponent) checkEligibilityComponent!: CheckEligibilityComponent;
  now = new Date();
  sideNavCollapsedWidth = 80;
  isSidenavCollapsed = false;
  isSliderSidenav = false;
  lastUpdatedWidth = 80;

  eligibilityModalVisible = false;
  eligibityCheckStep = 1;
  eligibityFormSubscription = Subscription.EMPTY;
  eligibilityForm = this.fb.group({
    formType: this.fb.control<string>(EligbilityFormType.NEW),
    appointmentId: -1,
    firstName: ['', ELIGIBILITY_FORM_VALIDATORS.firstName],
    lastName: ['', ELIGIBILITY_FORM_VALIDATORS.lastName],
    dateOfBirth: this.fb.control<Date>(null!, {
      validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
    }),
    zipCode: ['', ELIGIBILITY_FORM_VALIDATORS.zipCode],
    memberId: ['', ELIGIBILITY_FORM_VALIDATORS.memberId],
    groupId: ['', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
    appointmentDate: this.fb.control<Date>(new Date(), {
      validators: ELIGIBILITY_FORM_VALIDATORS.appointmentDate
    }),
    appointmentTime: [
      { hour: this.now.getHours(), minute: this.now.getMinutes() },
      ELIGIBILITY_FORM_VALIDATORS.appointmentTime
    ],
    provideUIType: ['SELECT'],
    provider: [-1, ELIGIBILITY_FORM_VALIDATORS.provider],
    providerFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
    providerLastName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
    npi: ['', ELIGIBILITY_FORM_VALIDATORS.providerNpi],
    taxId: ['', ELIGIBILITY_FORM_VALIDATORS.providerTaxId],
    insurancePayer: [-1, ELIGIBILITY_FORM_VALIDATORS.insurancePayer],
    procedureCodes: this.fb.control<string[]>([], {
      validators: ELIGIBILITY_FORM_VALIDATORS.procedureCodes
    }),
    subscriberFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.firstName],
    subscriberLastName: ['', ELIGIBILITY_FORM_VALIDATORS.lastName],
    subscriberDateOfBirth: this.fb.control<Date>(null!, {
      validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
    }),
    subscriberZipCode: ['', ELIGIBILITY_FORM_VALIDATORS.zipCode],
    subscriberMemberId: ['', ELIGIBILITY_FORM_VALIDATORS.memberId],
    subscriberGroupId: ['', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
    relationshipToSubscriber: [
      AppConstants.subscriberRelationShip.Others,
      ELIGIBILITY_FORM_VALIDATORS.relationshipToSubscriber
    ]
  });
  userData: UserData | null = null;
  currentLocationId = this.authService.getCurrentLocation();
  isRealTime = this.authService.getIsFeatureRealTime();
  navItems: { name: string; route: string; icon: DxcIcon }[] = [
    {
      name: 'Daily Huddle',
      route: 'eligibility/daily-huddle',
      icon: 'stethoscope'
    },
    {
      name: 'Reports',
      route: 'eligibility/reports',
      icon: 'chart'
    },
    {
      name: 'Patients',
      route: 'eligibility/patients',
      icon: 'user'
    },
    {
      name: 'Eligibility Complete',
      route: 'eligibility/eligibility-complete',
      icon: 'ballot-check'
    },
    {
      name: 'Administration',
      route: 'eligibility/admin',
      icon: 'sliders'
    }
  ];
  dentalLocations: DxcDropdownOption[] = [];
  windowWidth$ = new Subject<number>();
  providers: Provider[] = [];
  payers: Payer[] = [];
  procedurescode: ProcedureCode[] = [];
  submitLoading = false;
  editButtonChange = false;
  private isTemplateMode = false;

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private fb: NonNullableFormBuilder,
    readonly route: ActivatedRoute,
    readonly router: Router,
    private sharedService: SharedService,
    private appointmentService: AppointmentService,
    private eligibilityService: EligibilityService,
    readonly eligCompleteService: EligibilityCompleteService
  ) {
    this.isTemplateMode = this.route.snapshot.queryParams['isTemplateMode'] === 'true';
    this.userData = this.authService.getUserData();
    if (!this.authService.isEligibilityCompleteFeatureEnabled()) {
      this.navItems = this.navItems.filter((menuItem) => menuItem.name !== 'Eligibility Complete');
    }

    this.dentalLocations = this.userData.groups.map((group) => ({
      name: group.companyName,
      value: group.id
    }));

    if (
      !this.currentLocationId ||
      !this.userData?.groups.some((location) => location.id == this.currentLocationId)
    ) {
      const defaultGroup = AuthService.getDefaultLocation(this.userData.groups);
      if (defaultGroup?.id) this.currentLocationId = defaultGroup.id;
    }

    if (!this.isRealTime) {
      if (this.userData?.groups.length) {
        const group = this.userData?.groups.filter((obj) => obj.id === this.currentLocationId);
        if (group?.length) {
          this.isRealTime = AppConstants.eligibilityOAuthRequiredFeatures.some((e) =>
            group[0].features.includes(e)
          );
          this.authService.setIsFeatureRealTime(this.isRealTime);
        }
      }
    }
    if (!this.currentLocationId)
      Logger.error(`[${this.constructor.name}]`, 'Dental location not found');

    this.authService.setCurrentLocation(this.currentLocationId);

    Logger.trace(
      `[${this.constructor.name}]`,
      'current dental location id: ',
      this.currentLocationId
    );

    this.handleResize(window.innerWidth);
  }

  ngOnInit(): void {
    this.windowWidth$
      .pipe(untilDestroyed(this), throttleTime(300, undefined, { leading: true, trailing: true }))
      .subscribe((e) => {
        this.handleResize(e);
      });
    this.getAllProviders();
    this.getAllPayers();
    this.sharedService.getTagsListForce().pipe(untilDestroyed(this)).subscribe();
    this.sharedService.getAllProceduresCode().pipe(untilDestroyed(this)).subscribe();

    this.eligibilityService.onEligibilityCheckChange$.pipe(untilDestroyed(this)).subscribe(() => {
      this.getAllProviders();
    });

    this.sharedService.providers$.pipe(untilDestroyed(this)).subscribe((providers) => {
      this.providers = providers;
    });

    const group = this.authService
      .getUserData()
      .groups.find((group) => group.id === this.authService.getCurrentLocation());
    if (group?.companyName) this.sharedService.updateGroupName(group?.companyName).subscribe();
  }

  getAllProviders() {
    this.sharedService.getProvidersList().subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.providers = response.body.data.results;
        }
      }
    });
  }

  getAllPayers() {
    this.sharedService.getAllPayers().subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.payers = response.body.data;
        }
      }
    });
  }

  onNewEligibilityCheck() {
    this.checkEligibilityComponent.handleOpenEligibilityFormEvent({ type: 'NEW' });
  }

  changeDentalLocation(location: DxcDropdownOptionValue | DxcDropdownOptionValue[]) {
    if (Array.isArray(location)) {
      this.currentLocationId = Number(location);
      const group = this.userData?.groups.filter((obj) => obj.id === location[0]);
      if (group?.length) {
        this.isRealTime = AppConstants.eligibilityOAuthRequiredFeatures.some((e) =>
          group[0].features.includes(e)
        );
        this.authService.setIsFeatureRealTime(this.isRealTime);
      }
      this.authService.setCurrentLocation(this.currentLocationId);

      this.router.navigate([UrlConstants.eulaUrl]);
    }
  }

  logout() {
    this.authService.logout().subscribe();
  }

  @HostListener('window:resize', ['$event.target.innerWidth'])
  listenWindowWidth(e: number) {
    this.windowWidth$.next(e);
  }

  handleResize(e: number) {
    if (e === this.lastUpdatedWidth) return;
    this.lastUpdatedWidth = e;
    this.isSliderSidenav = e < SIDENAV_BREAKPOINT;
    this.sideNavCollapsedWidth = this.isSliderSidenav ? 240 : 80;
    if (this.isSliderSidenav) {
      this.isSidenavCollapsed = true;
    }
  }

  toggleSidenav(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.isSidenavCollapsed = !this.isSidenavCollapsed;
  }

  handleSidenavClickOutside() {
    if (!this.isSidenavCollapsed && this.isSliderSidenav) {
      this.isSidenavCollapsed = true;
    }
  }

  onNavigation() {
    if (this.isSliderSidenav) this.isSidenavCollapsed = true;
  }

  showHelpModal() {
    this.appService.lightHouseWidgetNavigate(
      environment.lightHouseWidget.home.page,
      environment.lightHouseWidget.home.pageId
    );
  }

  navigateToLandingPage() {
    window.location.href = environment.landingPageUrl;
  }

  onReviewCompleteRequest() {
    this.eligCompleteService._redirectToCompleteTab.next(true);
  }
}
